<template>

  <HeaderSection />

  <section id="formulario" class="py-12">

    <Form @submit="onSubmit" :validation-schema="schema" class="space-y-4 text-gray-700"
      @invalid-submit="onInvalidSubmit">

      <div class="container mx-auto px-4 max-w-6xl">

        <h1 class="font-bold text-gray-800 mb-0 text-3xl text-center pt-5 font-antonio-bold">
          ¡Bienvenidos/as a Córdoba Tech Week 2026!
        </h1>
        <h1 class="text-gray-800 mb-4 text-2xl text-center pt-2 pb-5 font-antonio-bold">
          Por favor completá el formulario de inscripción con todos tus datos
        </h1>

        <!-- SECCIÓN 1: DATOS PERSONALES -->
        <div class="flex flex-wrap space-y-4 md:space-y-0 border border-gray-500 rounded-md shadow-lg mb-6">
          <div class="w-full text-gray-800 text-2xl text-center font-antonio-bold py-4">
            Datos personales
          </div>
        </div>

        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
          <div class="w-full px-2 md:w-4/12">
            <label class="block mb-1 font-semibold">Nombre *</label>
            <Field name="nombre" v-slot="{ field, errors }" v-model="formData.nombre">
              <InputText v-bind="field" type="text" placeholder="Nombre" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="nombre" />
              </span>
            </Field>
          </div>
          <div class="w-full px-2 md:w-4/12">
            <label class="block mb-1 font-semibold">Apellido *</label>
            <Field name="apellido" v-slot="{ field, errors }" v-model="formData.apellido">
              <InputText v-bind="field" type="text" placeholder="Apellido" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="apellido" />
              </span>
            </Field>
          </div>
          <div class="w-full px-2 md:w-4/12">
            <label class="block mb-1 font-semibold">DNI *</label>
            <Field name="dni" v-slot="{ field, errors }" v-model="formData.dni">
              <InputText v-bind="field" type="text" placeholder="Número de DNI" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="dni" />
              </span>
            </Field>
          </div>
        </div>

        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
          <div class="w-full px-2 md:w-6/12">
            <label class="block mb-1 font-semibold">Correo electrónico *</label>
            <Field name="mail" v-slot="{ field, errors }" v-model="formData.mail">
              <InputText v-bind="field" type="email" placeholder="correo@ejemplo.com" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="mail" />
              </span>
            </Field>
          </div>
          <div class="w-full px-2 md:w-6/12">
            <label class="block mb-1 font-semibold">Teléfono celular (con código de área) *</label>
            <Field name="telefonoCelular" v-slot="{ field, errors }" v-model="formData.telefonoCelular">
              <InputText v-bind="field" type="text" placeholder="Número (ej: 3511234567)" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="telefonoCelular" />
              </span>
            </Field>
          </div>
        </div>

        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
          <div class="w-full px-2 md:w-4/12">
            <label class="block mb-1 font-semibold">Desde dónde nos visitás (País) *</label>
            <Field name="paisDomicilio" v-slot="{ handleChange, handleBlur, value, errors }"
              v-model="formData.paisDomicilio">
              <Select filter @blur="handleBlur" @update:model-value="handleChange" :model-value="value"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && value !== undefined, 'input-error': errors.length > 0 }"
                placeholder="País" class="input-base" :options="paises" optionLabel="descripcion">
                <template #value="slotProps">
                  <div v-if="slotProps.value" class="flex items-center">
                    <img v-if="slotProps.value.isocode" :src="getFlagUrl(slotProps.value.isocode)"
                      class="mr-2 border border-gray-200" style="width: 20px; height: auto;" />
                    <div>{{ slotProps.value.descripcion }}</div>
                  </div>
                  <span v-else>
                    {{ slotProps.placeholder }}
                  </span>
                </template>
                <template #option="slotProps">
                  <div class="flex items-center">
                    <img v-if="slotProps.option.isocode" :src="getFlagUrl(slotProps.option.isocode)"
                      class="mr-2 border border-gray-200" style="width: 20px; height: auto;" />
                    <div>{{ slotProps.option.descripcion }}</div>
                  </div>
                </template>
              </Select>
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="paisDomicilio" />
              </span>
            </Field>
          </div>

          <!-- Si visita desde Argentina -->
          <div class="w-full px-2 md:w-4/12" v-if="formData.paisDomicilio?.id === '+54'">
            <label class="block mb-1 font-semibold">Provincia *</label>
            <Field name="provincia" v-slot="{ handleChange, handleBlur, value, errors }" v-model="formData.provincia">
              <Select @blur="handleBlur" @update:model-value="handleChange" :model-value="value"
                placeholder="Seleccione la provincia" :options="provincias" optionLabel="descripcion" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && value !== undefined, 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="provincia" />
              </span>
            </Field>
          </div>

          <div class="w-full px-2 md:w-4/12" v-if="formData.paisDomicilio?.id === '+54'">
            <label class="block mb-1 font-semibold">Ciudad *</label>
            <Field name="localidad" v-slot="{ handleChange, handleBlur, value, errors }" v-model="formData.localidad">
              <Select @blur="handleBlur" filter @update:model-value="handleChange" :model-value="value"
                placeholder="Seleccione la ciudad" class="input-base" :options="localidades" optionLabel="descripcion"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && value !== undefined, 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="localidad" />
              </span>
            </Field>
          </div>

          <!-- Si visita desde Otro País -->
          <div class="w-full px-2 md:w-4/12" v-if="formData.paisDomicilio?.id !== '+54'">
            <label class="block mb-1 font-semibold">Provincia / Estado *</label>
            <Field name="provinciaEstado" v-slot="{ field, errors }" v-model="formData.provinciaEstado">
              <InputText v-bind="field" type="text" placeholder="Ingrese su provincia o estado" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="provinciaEstado" />
              </span>
            </Field>
          </div>

          <div class="w-full px-2 md:w-4/12" v-if="formData.paisDomicilio?.id !== '+54'">
            <label class="block mb-1 font-semibold">Ciudad *</label>
            <Field name="localidadCiudad" v-slot="{ field, errors }" v-model="formData.localidadCiudad">
              <InputText v-bind="field" type="text" placeholder="Ingrese su ciudad" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="localidadCiudad" />
              </span>
            </Field>
          </div>
        </div>

        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
          <div class="w-full px-2 md:w-6/12">
            <label class="block mb-1 font-semibold">Edad *</label>
            <Field name="rangoEtario" v-slot="{ handleChange, handleBlur, value, errors }"
              v-model="formData.rangoEtario">
              <Select @blur="handleBlur" @update:model-value="handleChange" :model-value="value"
                placeholder="Seleccione su rango de edad" class="input-base" :options="rangosEtarios"
                optionLabel="descripcion"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && value !== undefined, 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="rangoEtario" />
              </span>
            </Field>
          </div>
        </div>

        <!-- SECCIÓN 2: INFORMACIÓN INSTITUCIONAL Y PROFESIONAL -->
        <div class="flex flex-wrap space-y-4 md:space-y-0 border border-gray-500 rounded-md shadow-lg mt-11 mb-6">
          <div class="w-full text-gray-800 text-2xl text-center font-antonio-bold py-4">
            Información Institucional y Profesional
          </div>
        </div>

        <div class="space-y-4 mt-6 p-4 border border-blue-200 bg-blue-50/20 rounded-lg">
          <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
            <div class="w-full px-2 md:w-12/12">
              <label class="block mb-1 font-semibold">Nombre de la Institución a la que pertenece (Nombre de la Empresa, Organización, Universidad o Institución a la que pertenece) *</label>
              <Field name="empresa" v-slot="{ field, errors }" v-model="formData.empresa">
                <InputText v-bind="field" type="text" placeholder="Nombre de la Empresa, Organización, Universidad o Institución a la que pertenece"
                  class="input-base"
                  :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
                <span class="text-sm text-red-500 p-error">
                  <ErrorMessage name="empresa" />
                </span>
              </Field>
            </div>
          </div>

          <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
            <div class="w-full px-2 md:w-12/12">
              <label class="block mb-1 font-semibold">Profesión o Carrera en curso *</label>
              <Field name="profesionCarrera" v-slot="{ field, errors }" v-model="formData.profesionCarrera">
                <InputText v-bind="field" type="text" placeholder="Profesión o carrera en curso"
                  class="input-base"
                  :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
                <span class="text-sm text-red-500 p-error">
                  <ErrorMessage name="profesionCarrera" />
                </span>
              </Field>
            </div>
          </div>

          <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
            <div class="w-full px-2"
              :class="formData.nivelResponsabilidad?.id === 'Otro (especificar)' ? 'md:w-6/12' : 'md:w-12/12'">
              <label class="block mb-1 font-semibold">¿Cuál es tu nivel de responsabilidad? *</label>
              <Field name="nivelResponsabilidad" v-slot="{ handleChange, handleBlur, value, errors }"
                v-model="formData.nivelResponsabilidad">
                <Select @blur="handleBlur" @update:model-value="handleChange" :model-value="value"
                  placeholder="Seleccione su nivel de responsabilidad" class="input-base"
                  :options="nivelesResponsabilidad" optionLabel="descripcion"
                  :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && value !== undefined, 'input-error': errors.length > 0 }" />
                <span class="text-sm text-red-500 p-error">
                  <ErrorMessage name="nivelResponsabilidad" />
                </span>
              </Field>
            </div>
            <div class="w-full px-2 md:w-6/12" v-if="formData.nivelResponsabilidad?.id === 'Otro (especificar)'">
              <label class="block mb-1 font-semibold">Especificar nivel de responsabilidad *</label>
              <Field name="nivelResponsabilidadOtro" v-slot="{ field, errors }"
                v-model="formData.nivelResponsabilidadOtro">
                <InputText v-bind="field" type="text" placeholder="Especifique su nivel de responsabilidad"
                  class="input-base"
                  :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
                <span class="text-sm text-red-500 p-error">
                  <ErrorMessage name="nivelResponsabilidadOtro" />
                </span>
              </Field>
            </div>
          </div>

          <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
            <div class="w-full px-2"
              :class="formData.areaSector?.id === 'Otro (especificar)' ? 'md:w-6/12' : 'md:w-12/12'">
              <label class="block mb-1 font-semibold">Área o sector principal *</label>
              <Field name="areaSector" v-slot="{ handleChange, handleBlur, value, errors }"
                v-model="formData.areaSector">
                <Select @blur="handleBlur" @update:model-value="handleChange" :model-value="value"
                  placeholder="Seleccione el área o sector principal" class="input-base" :options="areasSector"
                  optionLabel="descripcion"
                  :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && value !== undefined, 'input-error': errors.length > 0 }" />
                <span class="text-sm text-red-500 p-error">
                  <ErrorMessage name="areaSector" />
                </span>
              </Field>
            </div>
            <div class="w-full px-2 md:w-6/12" v-if="formData.areaSector?.id === 'Otro (especificar)'">
              <label class="block mb-1 font-semibold">Especificar área o sector *</label>
              <Field name="areaSectorOtro" v-slot="{ field, errors }" v-model="formData.areaSectorOtro">
                <InputText v-bind="field" type="text" placeholder="Especifique el área o sector" class="input-base"
                  :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
                <span class="text-sm text-red-500 p-error">
                  <ErrorMessage name="areaSectorOtro" />
                </span>
              </Field>
            </div>
          </div>

          <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
            <div class="w-full px-2 md:w-12/12">
              <label class="block mb-1 font-semibold">LinkedIn profesional, redes sociales o sitio web institucional (opcional)</label>
              <Field name="redesSocialesLink" v-slot="{ field, errors }" v-model="formData.redesSocialesLink">
                <InputText v-bind="field" type="text" placeholder="https://linkedin.com/in/... o enlace web"
                  class="input-base"
                  :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
                <span class="text-sm text-red-500 p-error">
                  <ErrorMessage name="redesSocialesLink" />
                </span>
              </Field>
            </div>
          </div>
        </div>

        <!-- SECCIÓN 3: CÓMO TE ENTERASTE DEL EVENTO -->
        <div class="flex flex-wrap space-y-4 md:space-y-0 border border-gray-500 rounded-md shadow-lg mt-11 mb-6">
          <div class="w-full text-gray-800 text-2xl text-center font-antonio-bold py-4">
            ¿Cómo te enteraste del evento? *
          </div>
        </div>

        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
          <div class="w-full px-2" :class="mostrarMedioOtro ? 'md:w-6/12' : 'md:w-12/12'">
            <label class="block mb-1 font-semibold">Seleccione una o más opciones *</label>
            <Field name="mediosSeleccionados" v-slot="{ handleChange, handleBlur, value, errors }"
              v-model="formData.mediosSeleccionados">
              <MultiSelect @blur="handleBlur" @update:model-value="handleChange" :model-value="value"
                placeholder="Seleccione cómo se enteró del evento" class="input-base" :options="medios"
                optionLabel="descripcion" :showToggleAll="false"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && value !== undefined && value.length > 0, 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="mediosSeleccionados" />
              </span>
            </Field>
          </div>
          <div class="w-full px-2 md:w-6/12" v-if="mostrarMedioOtro">
            <label class="block mb-1 font-semibold">Otro medio (especificar) *</label>
            <Field name="medioOtro" v-slot="{ field, errors }" v-model="formData.medioOtro">
              <InputText v-bind="field" type="text" placeholder="Especifique cómo se enteró" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="medioOtro" />
              </span>
            </Field>
          </div>
        </div>

        <!-- SECCIÓN 4: SISTEMA DE REUNIONES (MEETING) -->
        <div v-if="!isEstudiante">
          <div class="flex flex-wrap space-y-4 md:space-y-0 border border-gray-500 rounded-md shadow-lg mt-11 mb-6">
            <div class="w-full text-gray-800 text-2xl text-center font-antonio-bold py-4">
              Sistema de Reuniones (Meeting)
            </div>
          </div>

          <div class="p-4 bg-gray-50 border rounded-lg">
            <div class="text-gray-800 font-semibold mb-3 text-base sm:text-lg">
              Crea tu contraseña para la plataforma meeting: esta te permitirá ser parte de espacios de vinculación y networking con otros asistentes y empresas del evento (obligatorio)*
            </div>

            <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-4">
              <div class="w-full px-2 md:w-6/12">
                <label class="block mb-1 font-semibold">Contraseña para meeting *</label>
                <Field name="password" v-slot="{ field, errors }" v-model="formData.password">
                  <InputText v-bind="field" type="password" placeholder="Contraseña de acceso" class="input-base"
                    :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
                  <span class="text-sm text-red-500 p-error">
                    <ErrorMessage name="password" />
                  </span>
                </Field>
              </div>
              <div class="w-full px-2 md:w-6/12">
                <label class="block mb-1 font-semibold">Confirmar contraseña *</label>
                <Field name="confirmPassword" v-slot="{ field, errors }" v-model="formData.confirmPassword">
                  <InputText v-bind="field" type="password" placeholder="Confirmá tu contraseña" class="input-base"
                    :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
                  <span class="text-sm text-red-500 p-error">
                    <ErrorMessage name="confirmPassword" />
                  </span>
                </Field>
              </div>
            </div>
          </div>
        </div>

        <!-- SECCIÓN 5: POLÍTICA DE PRIVACIDAD Y CONSENTIMIENTO -->
        <div class="flex flex-wrap space-y-4 md:space-y-0 border border-gray-500 rounded-md shadow-lg mt-11 mb-6">
          <div class="w-full text-gray-800 text-2xl text-center font-antonio-bold py-4">
            Política de Privacidad y Consentimiento
          </div>
        </div>

        <div class="space-y-6 bg-gray-50 p-6 rounded-lg border">
          <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-4 border-b border-gray-200">
            <span class="text-gray-800 font-semibold text-base">Términos y condiciones del evento</span>
            <button type="button" @click="mostrarModalPoliticas = true"
              class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition text-sm cursor-pointer">
              <i class="pi pi-file-o"></i> Ver políticas
            </button>
          </div>

          <!-- AUTORIZACIÓN DE USO (IMAGEN) -->
          <div>
            <label class="block font-bold text-gray-800 mb-2">
              Autorización de uso (selección obligatoria) *
            </label>
            <Field name="autorizoImagen" v-slot="{ handleChange, value, errors }" v-model="formData.autorizoImagen">
              <div class="flex items-start gap-3 cursor-pointer">
                <Checkbox :model-value="value" :binary="true" inputId="autorizoImagen"
                  @update:model-value="handleChange" :invalid="errors.length > 0" class="mt-0.5" />
                <label for="autorizoImagen" class="cursor-pointer text-sm text-gray-800 leading-snug">
                  Autorizo el uso de mi imagen para registros fotográficos y audiovisuales de Córdoba Tech Week y Córdoba Cluster.
                </label>
              </div>
              <span class="text-sm text-red-500 p-error block mt-1">
                <ErrorMessage name="autorizoImagen" />
              </span>
            </Field>
          </div>

          <!-- AUTORIZACIÓN DE POLÍTICA -->
          <div class="pt-4 border-t border-gray-200">
            <label class="block font-bold text-gray-800 mb-2">
              Autorización de política (selección obligatoria) *
            </label>
            <Field name="autorizoPolitica" v-slot="{ handleChange, value, errors }" v-model="formData.autorizoPolitica">
              <div class="flex items-start gap-3 cursor-pointer">
                <Checkbox :model-value="value" :binary="true" inputId="autorizoPolitica"
                  @update:model-value="handleChange" :invalid="errors.length > 0" class="mt-0.5" />
                <label for="autorizoPolitica" class="cursor-pointer text-sm text-gray-800 leading-snug">
                  Acepto la política de protección de datos personales y consiento el uso de la información brindada para fines exclusivamente organizativos y de vinculación de Córdoba Tech Week y Córdoba Cluster.
                </label>
              </div>
              <span class="text-sm text-red-500 p-error block mt-1">
                <ErrorMessage name="autorizoPolitica" />
              </span>
            </Field>
          </div>

          <!-- COMUNICACIONES -->
          <div class="pt-4 border-t border-gray-200">
            <label class="block font-bold text-gray-800 mb-2">
              📩 Comunicaciones (selección obligatoria) *
            </label>
            <Field name="comunicaciones" v-slot="{ handleChange, errors }" v-model="formData.comunicaciones">
              <div class="space-y-3" :class="{ 'text-red-600': errors.length > 0 }">
                <label class="flex items-start gap-3 cursor-pointer">
                  <RadioButton v-model="formData.comunicaciones" @update:model-value="handleChange"
                    name="comunicaciones" value="Si" class="mt-1" :invalid="errors.length > 0" />
                  <span class="text-sm text-gray-800 leading-snug">
                    Sí, quiero recibir novedades, actividades y contenidos de Córdoba Tech Week y Córdoba Cluster. Esto incluye confirmaciones, actualizaciones del cronograma, encuestas de satisfacción y futuras actividades vinculadas al ecosistema Córdoba Tech Week. Podés darte de baja en cualquier momento
                  </span>
                </label>
                <label class="flex items-start gap-3 cursor-pointer">
                  <RadioButton v-model="formData.comunicaciones" @update:model-value="handleChange"
                    name="comunicaciones" value="No" class="mt-1" :invalid="errors.length > 0" />
                  <span class="text-sm text-gray-800 leading-snug">
                    No quiero recibir novedades acerca el evento
                  </span>
                </label>
              </div>
              <span class="text-sm text-red-500 p-error block mt-1">
                <ErrorMessage name="comunicaciones" />
              </span>
            </Field>
          </div>
        </div>

        <div class="flex flex-wrap space-y-4 md:space-y-0 mt-6 py-4 border border-gray-500 rounded-md shadow-lg">
          <div class="w-full text-center font-semibold">
            Para más información, podés escribirnos a
          </div>
          <div class="w-full text-center">
            <a href="mailto:inscripciones@cordobacluster.com"
              class="text-blue-600 font-semibold underline">inscripciones@cordobacluster.com</a>
          </div>
        </div>

        <div class="flex flex-wrap space-y-4 md:space-y-0 mt-6 py-4 border border-gray-500 rounded-md shadow-lg">
          <div class="w-full text-center font-semibold">
            Te vamos a enviar un correo con más información del evento
          </div>
          <div class="w-full text-center font-semibold">
            Por favor verificá el mismo para completar tu acreditación
          </div>
        </div>

        <div class="flex flex-wrap py-2 mt-3">
          <button type="submit"
            class="mt-4 px-6 py-3 bg-transparent border-black hover:bg-black text-black font-semibold hover:text-white border-2 rounded text-lg transition duration-200 cursor-pointer"
            :disabled="isSubmitting">
            <i v-show="isSubmitting" class="pi pi-spin pi-spinner mr-2"></i>
            <i v-show="!isSubmitting" class="pi pi-check mr-2"></i>REGISTRARME
          </button>
        </div>

      </div>

      <BlockUI :blocked="isSubmitting" fullScreen />

    </Form>

    <!-- MODAL POLÍTICAS DE PRIVACIDAD -->
    <Dialog v-model:visible="mostrarModalPoliticas" modal header="Política de Privacidad y Consentimiento"
      :style="{ width: '90vw', maxWidth: '720px' }">
      <div class="space-y-4 text-sm text-gray-700 leading-relaxed max-h-[70vh] overflow-y-auto pr-2">
        <h3 class="font-bold text-base text-gray-900 border-b pb-2">Leer política de Privacidad y Consentimiento</h3>

        <div>
          <h4 class="font-bold text-gray-900 mb-1">Responsable del tratamiento</h4>
          <p>
            Los datos personales recabados a través del presente formulario serán tratados por <strong>Cluster
              Tecnológico Córdoba - Asociación Civil</strong>, CUIT 30-70782266-6, con domicilio en Avenida La Voz Del
            Interior 8851, piso 6, barrio Alta Córdoba, Córdoba Capital, en su carácter de organizador de Córdoba Tech
            Week 2026.
          </p>
        </div>

        <div>
          <h4 class="font-bold text-gray-900 mb-1">Finalidad del tratamiento de datos personales</h4>
          <p>Los datos proporcionados serán utilizados exclusivamente para:</p>
          <ul class="list-disc list-inside mt-1 space-y-1">
            <li>Gestionar la inscripción y acreditación a Córdoba Tech Week 2026;</li>
            <li>Organizar y coordinar la logística del evento y sus subeventos (Zona VIP, Cóctel, Ronda de Negocios,
              entre otros);</li>
            <li>Enviar confirmaciones, actualizaciones de cronograma y comunicaciones operativas vinculadas a la
              participación;</li>
            <li>En caso de haber prestado el consentimiento correspondiente, enviar novedades del ecosistema Córdoba
              Tech Week y compartir datos de contacto con empresas/instituciones aliadas.</li>
          </ul>
        </div>

        <div>
          <h4 class="font-bold text-gray-900 mb-1">Plazo de conservación</h4>
          <p>
            Los datos personales serán conservados durante el tiempo necesario para cumplir con las finalidades
            indicadas en la presente política de privacidad. En ningún caso los datos personales serán conservados ni
            utilizados para finalidades distintas de aquellas para las cuales el titular prestó su consentimiento.
          </p>
        </div>

        <div>
          <h4 class="font-bold text-gray-900 mb-1">Compartir datos con empresas e instituciones (debes seleccionar una opción)</h4>
          <p>
            Al inscribirme, doy mi consentimiento para que Córdoba Tech Week / Córdoba Cluster comparta mis datos de contacto con empresas, instituciones y organizaciones participantes o aliadas del evento.
          </p>
          <p class="mt-1">
            El participante reconoce y acepta que sus datos serán comunicados a terceros, quienes serán responsables del tratamiento que realicen de dichos datos, sin que Córdoba Tech Week / Córdoba Cluster asuma responsabilidad por el uso, tratamiento o finalidad que dichos terceros puedan darles, una vez efectuada la comunicación.
          </p>
        </div>

        <div class="space-y-3 pt-3 border-t border-gray-200 text-xs text-gray-600">
          <h4 class="font-bold text-gray-800 text-sm">A tener en cuenta:</h4>
          <p>
            <strong>Uso de imagen:</strong> Córdoba Tech Week 2026 es un evento masivo y abierto al público
            asistente, en el marco del cual se realizarán registros fotográficos y audiovisuales generales del evento
            (tomas de público, paneles, stands, actividades, etc.). Dicha captación se encuentra alcanzada por la
            excepción prevista en el art. 53, inc. c) del Código Civil y Comercial de la Nación (captación de imagen
            realizada en el marco de acontecimientos públicos o de interés general en los que participa el titular),
            por lo que no requiere consentimiento individual y podrá ser utilizada por Córdoba Tech Week y Córdoba
            Cluster con fines de cobertura, difusión institucional y promocional del evento (redes sociales, sitio
            web, publicidad y comunicaciones de futuras ediciones), en medios oficiales. Distinto es el caso de un uso
            individualizado o destacado de la imagen de una persona en particular (por ejemplo, entrevistas,
            testimoniales, piezas publicitarias centradas en un asistente identificado), para lo cual sí se solicitará
            el consentimiento específico de la persona en el momento correspondiente.
          </p>
          <p>
            <strong>Derechos de los titulares:</strong> El titular de los datos personales tiene la facultad de
            ejercer el derecho de acceso a los mismos en términos de la Ley 25.326. Asimismo, podrá solicitar la
            rectificación, actualización o supresión de sus datos, y revocar en cualquier momento el consentimiento
            prestado, escribiendo a <a href="mailto:info@cordobacluster.com"
              class="text-blue-600 underline">info@cordobacluster.com</a>, sin efecto retroactivo sobre los
            tratamientos ya realizados de forma lícita con anterioridad a la revocación.
          </p>
          <p>
            En particular, y conforme a lo ya señalado, la revocación no habilita a exigir la eliminación de material de cobertura general del
            evento (fotos, videos institucionales, piezas de difusión masiva), dado que dicha captación no depende del
            consentimiento individual sino de la excepción legal aplicable.
          </p>
        </div>
      </div>
      <template #footer>
        <button type="button" @click="mostrarModalPoliticas = false"
          class="px-4 py-2 bg-gray-800 hover:bg-black text-white rounded-md text-sm cursor-pointer">
          Entendido
        </button>
      </template>
    </Dialog>

  </section>

</template>

<script lang="ts">
import * as yup from 'yup';
import { defineComponent } from 'vue';
import { Field, Form, ErrorMessage } from 'vee-validate';
import Swal from 'sweetalert2';

import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import RadioButton from 'primevue/radiobutton';
import Checkbox from 'primevue/checkbox';
import Dialog from 'primevue/dialog';
import BlockUI from 'primevue/blockui';
import HeaderSection from './HeaderSection.vue';

import jsonCountries from '@/data/countries.json';
import jsonMedios from '@/data/medios.json';
import jsonNivelesResponsabilidad from '@/data/nivelesResponsabilidad.json';
import jsonAreasSector from '@/data/areasSector.json';
import jsonRangosEtarios from '@/data/rangosEtarios.json';
import jsonProvincias from '@/data/provincias.json';
import jsonLocalidades from '@/data/localidades.json';

import suiteService, { Acreditado, TipoAcreditado, EstadoAcreditado, IdLabelEntity, StringIdLabelEntity, OrigenAcreditado } from 'qreventos-suite/services';
import suiteConfig from '@/services/suite.config';
import { SAVE, ORIGEN_ACREDITADO, VERIFICADO } from "qreventos-suite/constantes";

interface Pais extends StringIdLabelEntity {
  isocode?: string;
}
interface Medio extends StringIdLabelEntity { }
interface NivelResponsabilidad extends StringIdLabelEntity { }
interface AreaSector extends StringIdLabelEntity { }
interface RangoEtario extends StringIdLabelEntity { }
interface Provincia extends IdLabelEntity { }
interface Localidad extends IdLabelEntity { }

export default defineComponent({
  components: {
    HeaderSection,
    InputText, Select, MultiSelect, RadioButton, Checkbox, Dialog,
    Field, ErrorMessage, Form, BlockUI
  },
  created() {
    document.title = "Córdoba TechWeek 2026";
  },
  data() {
    return {
      paises: [] as Pais[],
      provincias: [] as Provincia[],
      localidades: [] as Localidad[],
      nivelesResponsabilidad: [] as NivelResponsabilidad[],
      areasSector: [] as AreaSector[],
      medios: [] as Medio[],
      rangosEtarios: [] as RangoEtario[],
      mostrarModalPoliticas: false,
      isSubmitting: false,
      formData: {
        nombre: '',
        apellido: '',
        dni: '',
        mail: '',
        telefonoCelular: '',
        paisDomicilio: { id: "+54", descripcion: "Argentina", isocode: "AR" } as Pais,
        provincia: undefined as unknown as Provincia,
        provinciaEstado: '',
        localidad: undefined as unknown as Localidad,
        localidadCiudad: '',
        rangoEtario: undefined as unknown as RangoEtario,

        // Información Institucional y Profesional
        empresa: '',
        profesionCarrera: '',
        nivelResponsabilidad: undefined as unknown as NivelResponsabilidad,
        nivelResponsabilidadOtro: '',
        areaSector: undefined as unknown as AreaSector,
        areaSectorOtro: '',
        redesSocialesLink: '',

        // Medios
        mediosSeleccionados: [] as Medio[],
        medioOtro: '',

        // Reuniones Meeting
        password: '',
        confirmPassword: '',

        // Consentimientos y Políticas
        autorizoImagen: false,
        autorizoPolitica: false,
        comunicaciones: '' as 'Si' | 'No' | ''
      }
    };
  },
  computed: {
    mostrarMedioOtro(): boolean {
      return this.formData.mediosSeleccionados?.some(m => m.id === 'Otro (especificar)') ?? false;
    },
    isEstudiante(): boolean {
      return this.formData.nivelResponsabilidad?.id === 'Estudiante';
    },
    schema() {
      return yup.object({
        nombre: yup.string().required('Ingrese su nombre').min(3, 'Al menos 3 letras'),
        apellido: yup.string().required('Ingrese su apellido').min(3, 'Al menos 3 letras'),
        dni: yup.string().required('Ingrese su DNI').matches(/^\d+$/, 'Solo números').min(6, 'Al menos 6 números'),
        mail: yup.string().required('Ingrese su correo').email('Correo inválido'),
        telefonoCelular: yup.string().required('Ingrese el celular').matches(/^\d+$/, 'Solo se permiten números').min(8, 'Deben ser un mínimo de 8 números').max(20, 'Deben ser menos de 20 números'),
        paisDomicilio: yup.mixed().required('Seleccione el país'),

        provincia: yup.mixed().test('val-provincia', 'Seleccione la provincia', function (value) {
          const pais = this.parent.paisDomicilio;
          if (pais?.id === '+54' && !value) return false;
          return true;
        }),

        localidad: yup.mixed().test('val-ciudad-ar', 'Seleccione la ciudad', function (value) {
          const pais = this.parent.paisDomicilio;
          if (pais?.id === '+54' && !value) return false;
          return true;
        }),

        provinciaEstado: yup.string().test('val-prov-otro', 'Ingrese la provincia o estado', function (value) {
          const pais = this.parent.paisDomicilio;
          if (pais?.id !== '+54' && (!value || value.trim() === '')) return false;
          return true;
        }),

        localidadCiudad: yup.string().test('val-ciudad-otro', 'Ingrese la ciudad', function (value) {
          const pais = this.parent.paisDomicilio;
          if (pais?.id !== '+54' && (!value || value.trim() === '')) return false;
          return true;
        }),

        rangoEtario: yup.mixed().required('Seleccione su rango de edad'),

        // Institución y Profesión
        empresa: yup.string().required('Ingrese la institución a la que pertenece'),
        profesionCarrera: yup.string().required('Ingrese su profesión o carrera en curso'),

        nivelResponsabilidad: yup.mixed().required('Seleccione su nivel de responsabilidad'),
        nivelResponsabilidadOtro: yup.string().test('val-nivel-resp-otro', 'Especifique su nivel de responsabilidad', function (value) {
          const nivel = this.parent.nivelResponsabilidad;
          if (nivel?.id === 'Otro (especificar)' && (!value || value.trim() === '')) return false;
          return true;
        }),

        areaSector: yup.mixed().required('Seleccione el área o sector principal'),
        areaSectorOtro: yup.string().test('val-area-sector-otro', 'Especifique el área o sector', function (value) {
          const area = this.parent.areaSector;
          if (area?.id === 'Otro (especificar)' && (!value || value.trim() === '')) return false;
          return true;
        }),

        redesSocialesLink: yup.string().optional(),

        // Medios
        mediosSeleccionados: yup.array().min(1, 'Seleccione al menos un medio'),
        medioOtro: yup.string().test('val-medio-otro', 'Especifique el medio', function (value) {
          const seleccionados = this.parent.mediosSeleccionados as Medio[];
          const tieneOtro = seleccionados?.some((m: Medio) => m.id === 'Otro (especificar)');
          if (tieneOtro && (!value || value.trim() === '')) return false;
          return true;
        }),

        // Reuniones Meeting (requerido si no es Estudiante)
        password: yup.string().test('val-passwd', 'Ingrese una contraseña (mínimo 4 caracteres)', function (value) {
          const nivel = this.parent.nivelResponsabilidad;
          if (nivel?.id !== 'Estudiante' && (!value || value.length < 4)) return false;
          return true;
        }),

        confirmPassword: yup.string().test('val-confirm-passwd', function (value) {
          const nivel = this.parent.nivelResponsabilidad;
          if (nivel?.id !== 'Estudiante') {
            if (!value) return this.createError({ message: 'Confirme su contraseña' });
            if (value !== this.parent.password) return this.createError({ message: 'Las contraseñas no coinciden' });
          }
          return true;
        }),

        // Consentimientos
        autorizoImagen: yup.boolean().oneOf([true], 'Debe autorizar el uso de su imagen').required('Debe autorizar el uso de su imagen'),
        autorizoPolitica: yup.boolean().oneOf([true], 'Debe aceptar la política de protección de datos personales').required('Debe aceptar la política de protección de datos personales'),
        comunicaciones: yup.string().required('Debe seleccionar una opción sobre comunicaciones')
      });
    }
  },
  watch: {
    'formData.provincia.id'(newValue) {
      if (newValue) {
        this.localidades = jsonLocalidades.filter((localidad: Localidad) => localidad.id === newValue);
      } else {
        this.localidades = [];
      }
      this.formData.localidad = undefined as unknown as Localidad;
    },
    'formData.nivelResponsabilidad.id'(newValue) {
      if (newValue !== 'Otro (especificar)') {
        this.formData.nivelResponsabilidadOtro = '';
      }
      if (newValue === 'Estudiante') {
        this.formData.password = '';
        this.formData.confirmPassword = '';
      }
    },
    'formData.areaSector.id'(newValue) {
      if (newValue !== 'Otro (especificar)') {
        this.formData.areaSectorOtro = '';
      }
    },
    'formData.mediosSeleccionados'(newValue: Medio[]) {
      const tieneOtro = newValue?.some((m: Medio) => m.id === 'Otro (especificar)');
      if (!tieneOtro) {
        this.formData.medioOtro = '';
      }
    }
  },
  methods: {
    onInvalidSubmit(values: any) {
      const errores: number = Object.values(values?.errors || {}).flat().length;
      Swal.fire({
        title: "Hay campos incompletos",
        html: `Por favor revise el formulario y complete todos los campos obligatorios (${errores} pendiente/s)`,
        icon: "error"
      });
    },
    getFlagUrl(isocode: string) {
      if (!isocode) return '';
      try {
        return require(`@/assets/flags/${isocode.toLowerCase()}.svg`);
      } catch (e) {
        return '';
      }
    },
    async validarDni() {
      try {
        const dni = this.formData.dni ? this.formData.dni.trim() : '';
        const acreditadoExistente: Acreditado = await suiteService.findAcreditadoByFilters(suiteConfig.evento, "dni=" + encodeURIComponent(dni)) as Acreditado;
        if (acreditadoExistente && (acreditadoExistente.id || acreditadoExistente.dni)) {
          Swal.fire({ title: "Atención", html: "El DNI ingresado ya fue registrado", icon: "warning" });
          return false;
        }
        return true;
      } catch (error) {
        Swal.fire({ title: "Error", text: error + '', icon: "error" });
        return false;
      }
    },
    async validarMail() {
      try {
        const mail = this.formData.mail ? this.formData.mail.trim() : '';
        const acreditadoExistente: Acreditado = await suiteService.findAcreditadoByFilters(suiteConfig.evento, "mail=" + encodeURIComponent(mail)) as Acreditado;
        if (acreditadoExistente && (acreditadoExistente.id || acreditadoExistente.mail)) {
          Swal.fire({ title: "Atención", html: "El correo electrónico ingresado ya fue registrado", icon: "warning" });
          return false;
        }
        return true;
      } catch (error) {
        Swal.fire({ title: "Error", text: error + '', icon: "error" });
        return false;
      }
    },
    async onSubmit() {
      this.isSubmitting = true;

      if (!this.formData.autorizoPolitica || !this.formData.autorizoImagen) {
        this.isSubmitting = false;
        Swal.fire({
          title: "Atención",
          html: "Para registrarse en el evento debe autorizar el uso de imagen y aceptar la política de protección de datos",
          icon: "error"
        });
        return;
      }

      const dniValido = await this.validarDni();
      if (!dniValido) {
        this.isSubmitting = false;
        return;
      }

      const emailValido = await this.validarMail();
      if (!emailValido) {
        this.isSubmitting = false;
        return;
      }

      const acreditado: Acreditado = new Object() as Acreditado;

      // Determinación del tipo de acreditado:
      // Si es Estudiante -> id = 2704 (sin reuniones)
      // Demás niveles de responsabilidad -> id = 2679 (con reuniones)
      if (this.formData.nivelResponsabilidad?.id === 'Estudiante') {
        acreditado.tipoAcreditado = { id: 2704 } as TipoAcreditado;
      } else {
        acreditado.tipoAcreditado = { id: 2679 } as TipoAcreditado;
        acreditado.passwd = this.formData.password;
        (acreditado as any).password = this.formData.password;
      }

      acreditado.estadoAcreditado = { id: VERIFICADO } as EstadoAcreditado;
      acreditado.origenAcreditado = { id: ORIGEN_ACREDITADO } as OrigenAcreditado;
      acreditado.nombre = this.formData.nombre;
      acreditado.apellido = this.formData.apellido;
      acreditado.dni = this.formData.dni ? this.formData.dni.trim() : '';
      acreditado.mail = this.formData.mail ? this.formData.mail.trim() : '';
      acreditado.telefono = this.formData.telefonoCelular;
      acreditado.empresa = this.formData.empresa;
      acreditado.cargo = '';
      acreditado.modificado = SAVE;
      acreditado.cantidad = 1;
      acreditado.notificar = true;
      acreditado.notificarWhatsApp = false;

      // Mapeo datoxx
      acreditado.dato1 = this.formData.paisDomicilio?.descripcion;
      if (this.formData.paisDomicilio?.id === '+54') {
        acreditado.dato2 = this.formData.provincia?.descripcion;
        acreditado.dato3 = this.formData.localidad?.descripcion; // Ciudad de localidades.json
      } else {
        acreditado.dato12 = this.formData.provinciaEstado;
        acreditado.dato13 = this.formData.localidadCiudad;
      }

      acreditado.dato4 = this.formData.rangoEtario?.id;

      // Información Institucional y Profesional
      acreditado.dato5 = this.formData.profesionCarrera;
      acreditado.dato17 = this.formData.nivelResponsabilidad?.id;
      acreditado.dato18 = this.formData.nivelResponsabilidad?.id === 'Otro (especificar)' ? this.formData.nivelResponsabilidadOtro : '';
      acreditado.dato6 = this.formData.areaSector?.id;
      acreditado.dato19 = this.formData.areaSector?.id === 'Otro (especificar)' ? this.formData.areaSectorOtro : '';
      acreditado.dato10 = this.formData.redesSocialesLink || '';

      // Medios
      acreditado.dato7 = this.formData.mediosSeleccionados?.map((m: Medio) => m.descripcion).join(', ');
      acreditado.dato11 = this.formData.mediosSeleccionados?.some((m: Medio) => m.id === 'Otro (especificar)') ? this.formData.medioOtro : '';

      // Consentimientos (nota: no se guarda dato21)
      acreditado.dato8 = this.formData.comunicaciones;
      acreditado.dato9 = "Si"; // Aceptación integral marco normativo

      try {
        await suiteService.saveOrUpdate(suiteConfig.evento, acreditado);
        Swal.fire({
          title: "Acreditación exitosa",
          html: "Estimado/a:<br>Hemos recibido sus datos de registración con éxito.<br/><br/>Le enviamos un mail con un enlace para descargar su credencial para concurrir al evento.<br/><br/>Por favor revise su casilla de correo y de spam.",
          icon: "success"
        }).then(() => {
          this.$router.push('/finalizado');
        });
      } catch (error) {
        Swal.fire({ title: "Error", text: error + '', icon: "error" });
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  async mounted() {
    this.paises = jsonCountries;
    this.nivelesResponsabilidad = jsonNivelesResponsabilidad;
    this.areasSector = jsonAreasSector;
    this.medios = jsonMedios;
    this.rangosEtarios = jsonRangosEtarios;
    this.provincias = jsonProvincias;

    const argentina = this.paises.find(p => p.id === '+54');
    if (argentina) {
      this.formData.paisDomicilio = argentina;
    }
  }
});

</script>
